/**
 * The transport: step back, play/pause, step forward, elapsed time and speed.
 * It owns no playback state — the surrounding view runs the clock and hands
 * the numbers down.
 */
import { Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatClock } from './time';

export type PlaybackSpeed = 1 | 2 | 4 | 8;

const SPEEDS: PlaybackSpeed[] = [1, 2, 4, 8];

interface Props {
  playing: boolean;
  speed: PlaybackSpeed;
  onTogglePlay: () => void;
  onChangeSpeed: (speed: PlaybackSpeed) => void;
  onStep?: (direction: -1 | 1) => void;
  /** Playhead position and total length, in ms since the first event. */
  cursorMs?: number;
  durationMs?: number;
  disabled?: boolean;
}

export function ReplayPlaybackControls({
  playing,
  speed,
  onTogglePlay,
  onChangeSpeed,
  onStep,
  cursorMs = 0,
  durationMs = 0,
  disabled = false,
}: Props) {
  return (
    <div role="toolbar" aria-label="Playback controls" className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Previous event"
        disabled={disabled || !onStep}
        onClick={() => onStep?.(-1)}
      >
        <SkipBack />
      </Button>

      <Button
        size="icon"
        data-testid="replay-play-pause"
        aria-label={playing ? 'Pause playback' : 'Play playback'}
        aria-pressed={playing}
        disabled={disabled}
        onClick={onTogglePlay}
        className="rounded-full"
      >
        {playing ? <Pause /> : <Play />}
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Next event"
        disabled={disabled || !onStep}
        onClick={() => onStep?.(1)}
      >
        <SkipForward />
      </Button>

      <div className="ml-1 text-meta tabular-nums">
        <span className="font-semibold text-ink">{formatClock(cursorMs)}</span>
        <span className="text-ink-faint"> / {formatClock(durationMs)}</span>
      </div>

      {/* Speed. Plain toggle buttons rather than the shared Segmented: these
          are three independent pressed states, not a tab set. */}
      <div className="ml-auto inline-flex gap-0.5 rounded-control bg-muted p-0.5" role="group" aria-label="Playback speed">
        {SPEEDS.map((s) => {
          const active = s === speed;
          return (
            <button
              key={s}
              type="button"
              data-testid={`replay-speed-${s}`}
              aria-pressed={active}
              aria-label={`Playback speed ${s} times`}
              disabled={disabled}
              onClick={() => onChangeSpeed(s)}
              className={cn(
                'rounded-[7px] px-2.5 py-1 text-meta font-semibold transition-colors disabled:opacity-50',
                active ? 'bg-card text-ink shadow-panel' : 'text-ink-dim hover:text-ink',
              )}
            >
              {s}×
            </button>
          );
        })}
      </div>
    </div>
  );
}
